'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

const BRAND = '#3757FF';

export default function LearnHero() {
    const [mounted, setMounted] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        if (!mounted) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        let animId: number;
        let t = 0;
        const resize = () => {
            canvas.width = canvas.offsetWidth * window.devicePixelRatio;
            canvas.height = canvas.offsetHeight * window.devicePixelRatio;
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        };
        resize();
        window.addEventListener('resize', resize);
        const draw = () => {
            const w = canvas.offsetWidth;
            const h = canvas.offsetHeight;
            ctx.clearRect(0, 0, w, h);
            t += 0.003;
            const orbs = [
                { x: w * 0.15 + Math.sin(t) * 70, y: h * 0.4 + Math.cos(t * 0.7) * 50, r: 350, color: 'rgba(55,87,255,0.07)' },
                { x: w * 0.85 + Math.cos(t * 0.8) * 60, y: h * 0.5 + Math.sin(t * 1.1) * 60, r: 380, color: 'rgba(55,87,255,0.05)' },
                { x: w * 0.5 + Math.sin(t * 1.2) * 90, y: h * 0.1 + Math.cos(t * 0.6) * 40, r: 250, color: 'rgba(100,130,255,0.06)' },
            ];
            orbs.forEach(orb => {
                const grad = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.r);
                grad.addColorStop(0, orb.color);
                grad.addColorStop(1, 'transparent');
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(orb.x, orb.y, orb.r, 0, Math.PI * 2);
                ctx.fill();
            });
            animId = requestAnimationFrame(draw);
        };
        draw();
        return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
    }, [mounted]);

    return (
        <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20 px-6 md:px-10" style={{ background: '#FAFAFA' }}>
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

            <div className="max-w-7xl mx-auto relative z-10 w-full">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left */}
                    <div>
                        <div className="flex items-center gap-3 mb-8">
                            <Link href="/wisly" className="text-xs font-700 uppercase tracking-widest transition-colors" style={{ color: '#999', letterSpacing: '0.12em' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = BRAND; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#999'; }}
                            >
                                Wisly
                            </Link>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 2l4 4-4 4" stroke="#CCC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            <span className="text-xs font-700 uppercase tracking-widest" style={{ color: BRAND, letterSpacing: '0.12em' }}>Wisly Learn</span>
                        </div>

                        <h1
                            className="font-display font-800 mb-6"
                            style={{ fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)', lineHeight: 1.0, letterSpacing: '-0.03em', color: '#0D0D0D' }}
                        >
                            Redefining
                            <br />
                            <span style={{ color: BRAND }}>how you learn.</span>
                        </h1>

                        <p className="text-xl leading-relaxed mb-4" style={{ color: '#444', maxWidth: '520px' }}>
                            Wisly Learn is the learner-facing experience — a connected space where students and parents discover programs, join live classes, track progress, and engage with educators.
                        </p>

                        <div
                            className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl mb-10"
                            style={{ background: 'rgba(55,87,255,0.06)', border: '1px solid rgba(55,87,255,0.12)' }}
                        >
                            {['Discover', 'Learn', 'Connect', 'Grow'].map((word, i) => (
                                <React.Fragment key={word}>
                                    <span className="text-sm font-700" style={{ color: BRAND }}>{word}</span>
                                    {i < 3 && <span className="w-1 h-1 rounded-full" style={{ background: 'rgba(55,87,255,0.35)' }} />}
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <a
                                href="#discover"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-sm font-700 text-white transition-all duration-300"
                                style={{ background: BRAND, boxShadow: '0 8px 28px rgba(55,87,255,0.28)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 36px rgba(55,87,255,0.35)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 28px rgba(55,87,255,0.28)'; }}
                            >
                                Explore the experience
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7h9M7.5 3.5l3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </a>
                            <a
                                href="#for-parents"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-sm font-600 transition-all duration-300"
                                style={{ color: '#333', border: '1.5px solid rgba(13,13,13,0.15)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = BRAND; (e.currentTarget as HTMLElement).style.color = BRAND; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(13,13,13,0.15)'; (e.currentTarget as HTMLElement).style.color = '#333'; }}
                            >
                                For parents
                            </a>
                        </div>
                    </div>

                    {/* Right: Dashboard mockup */}
                    <div className="relative">
                        <div
                            className="rounded-3xl p-6 relative overflow-hidden"
                            style={{ background: 'white', boxShadow: '0 24px 80px rgba(55,87,255,0.12)', border: '1px solid rgba(55,87,255,0.1)' }}
                        >
                            {/* Header bar */}
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <p className="text-xs font-700 uppercase tracking-widest mb-1" style={{ color: '#999', letterSpacing: '0.12em' }}>My Dashboard</p>
                                    <p className="text-lg font-800" style={{ color: '#0D0D0D' }}>Good morning, Ama 👋</p>
                                </div>
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-800 text-white" style={{ background: BRAND }}>A</div>
                            </div>

                            {/* Progress card */}
                            <div className="rounded-2xl p-5 mb-4" style={{ background: '#F7F8FF', border: '1px solid rgba(55,87,255,0.1)' }}>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-700" style={{ color: '#0D0D0D' }}>Mathematics — Grade 10</p>
                                    <span className="text-xs font-700 px-2.5 py-1 rounded-full" style={{ background: 'rgba(55,87,255,0.1)', color: BRAND }}>In Progress</span>
                                </div>
                                <div className="w-full h-2 rounded-full mb-2" style={{ background: 'rgba(55,87,255,0.1)' }}>
                                    <div className="h-2 rounded-full" style={{ width: '72%', background: BRAND }} />
                                </div>
                                <p className="text-xs font-600" style={{ color: '#888' }}>72% complete · 3 modules remaining</p>
                            </div>

                            {/* Live session */}
                            <div className="rounded-2xl p-4 mb-4 flex items-center gap-4" style={{ background: BRAND }}>
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" fill="white" /><circle cx="8" cy="8" r="6" stroke="white" strokeWidth="1.5" opacity="0.5" /></svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-700 text-white">Live Now: Algebra Fundamentals</p>
                                    <p className="text-xs font-500" style={{ color: 'rgba(255,255,255,0.7)' }}>Mr. Osei · 24 students joined</p>
                                </div>
                                <span className="text-xs font-700 px-3 py-1.5 rounded-lg text-white" style={{ background: 'rgba(255,255,255,0.2)' }}>Join</span>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-3 gap-3">
                                {[{ label: 'Programs', value: '12' }, { label: 'Hours', value: '48' }, { label: 'Avg Score', value: '94%' }].map(stat => (
                                    <div key={stat.label} className="rounded-xl p-4 text-center" style={{ background: '#F7F8FF', border: '1px solid rgba(55,87,255,0.08)' }}>
                                        <p className="text-xl font-800 mb-0.5" style={{ color: BRAND }}>{stat.value}</p>
                                        <p className="text-xs font-600" style={{ color: '#888' }}>{stat.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Floating badge */}
                        <div
                            className="absolute -bottom-4 -left-4 rounded-2xl px-5 py-3 flex items-center gap-3"
                            style={{ background: 'white', boxShadow: '0 8px 32px rgba(55,87,255,0.15)', border: '1px solid rgba(55,87,255,0.1)' }}
                        >
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(55,87,255,0.1)' }}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 10l3-4 2.5 2.5 3-4.5 2.5 3" stroke={BRAND} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </div>
                            <div>
                                <p className="text-xs font-800" style={{ color: '#0D0D0D' }}>Progress this week</p>
                                <p className="text-xs font-600" style={{ color: '#888' }}>+18% from last week</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
